import { Card, Col, Row, Typography, Button, Tooltip, Modal, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { ArrowLeftOutlined, DeleteOutlined } from "@ant-design/icons";

import subSectionApi from "../../services/subSectionApi";
import { getPlainTextSummary } from "../../utils/HtmlHelper";
import PageLoader from "../../components/custom/PageLoader";
import SectionMobilePreviewPage from "./SectionMobilePreview";
import NewSectionPopup from './NewSectionPopup';
import {
    EditOutlined
} from "@ant-design/icons";
import sectionApi,{SectionPatchBody} from '../../services/sectionApi';

const { Title } = Typography;

const SectionPage: React.FC = () => {
  const location = useLocation();
  const { sectionId, sectionName: initialSectionName } = location.state || {};
  const [currentSectionName, setCurrentSectionName] = useState(initialSectionName);
  const navigate = useNavigate();
  const [subSectionCards, setSubSectionCards] = useState<any[]>([]);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; subSectionId?: string }>({ open: false });
  const [messageApi, contextHolder] = message.useMessage();
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [showMobilePreview, setShowMobilePreview] = useState<boolean>(false);
  const [showUpdateSectionPopup, setShowUpdateSectionPopup] = useState<boolean>(false);
  const [isUpdateSectionSaving, setIsUpdateSectionSaving] = useState(false);

  const handleNewSubSection = () => {
    navigate("/subsection", {
      state: {
        operationName: "add",
        sectionId: sectionId,
        sectionName: currentSectionName,
        subSectionId: "",
      },
    });
  };

  const handleSubSectionDelete = async (subSectionId: string) => {
    try {
      const response = await subSectionApi.deleteSubSection(subSectionId);
      if (response.status === 200) {
        setDeleteModal({ open: false });
        messageApi.success(`Subsection deleted successfully!`);
        await getSubSectionItems();
      }
    } catch (err) {
      messageApi.error("Something went wrong");
      console.error("Failed to load data:", err);
    } finally {
      setDeleteModal({ open: false });
    }
  };

  useEffect(() => {
    const subSectionCards = async () => {
      try {
        await getSubSectionItems();
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setIsPageLoading(false);
      }
    };
    subSectionCards();
  }, []);

  const getSubSectionItems = async () => {
    try {
      const subSectionItems = await subSectionApi.getSubSection(sectionId);
      setSubSectionCards(subSectionItems);
    } catch (error) {
      console.error("Error loading items:", error);
    }
  };

  const handleEditSectionName = async () => {
			setShowUpdateSectionPopup(true);
	  };

  const closeUpdateSectionPopup = async () => {
    setShowUpdateSectionPopup(false);
  };

  const handleDiscardSection = async () => {
    setShowUpdateSectionPopup(false);
  };

  const handleUpdateSection = async (sectionTitle: string) => {
    setIsUpdateSectionSaving(true);

    try {
      const sectionUpdateBody : SectionPatchBody = {
        id: sectionId,
        sectionName : sectionTitle,
      };
      const response = await sectionApi.patchSection(sectionUpdateBody);
      if (response.status == 200) {
        messageApi.success(`Section Name updated successfully.`);
        setCurrentSectionName(sectionTitle);
        setShowUpdateSectionPopup(false);
      }
    } catch (error) {
      const apiErrorMessage = error?.response?.data?.Message;
      messageApi.error(apiErrorMessage || 'Something went wrong');
    }
    finally {
      setIsUpdateSectionSaving(false);
    }
  };

  return (
    <div>
      {contextHolder}

      {isPageLoading && <PageLoader />}

      {/* Delete Modal */}
      <Modal
        title="Are you sure you want to delete the subsection?"
        open={deleteModal.open}
        onOk={() => deleteModal.subSectionId && handleSubSectionDelete(deleteModal.subSectionId)}
        onCancel={() => setDeleteModal({ open: false })}
        okText="Yes"
        cancelText="No"
        centered
        okButtonProps={{
            style: styles.okButton,
            type: "default",
        }}
      />

      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Typography.Title level={3} style={{ margin: 0, color: "#41787C" }}>
                        <Button
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(-1)}
                            style={{ marginBottom: 10, padding: 0 }}
                        />
                        {currentSectionName}
                    </Typography.Title>
                </Col>
                    <Col>
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            onClick={() => handleEditSectionName()}
                            style={{
                              backgroundColor: "#3E767A",
                              borderColor: "#3E767A",
                              color: "#fff",
                            }}
                        >Edit Section Name
                        </Button>
                    </Col>
        </Row>

      {/* Cards Grid */}
      <div>
        <Row gutter={[16, 16]}>
          {subSectionCards && subSectionCards.map((card) => (
            <Col xs={24} sm={12} md={8} lg={6} key={card.key}>
              <Card
                hoverable
                cover={<img src={card.fileUrl} alt={card.name} style={{ height: "250px" }} />}
                onClick={() =>
                  navigate("/subsection", {
                    state: {
                      operationName: "update",
                      sectionId: sectionId,
                      sectionName: currentSectionName,
                      subSectionId: card.id,
                    },
                  })
                }
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {/* Name and Delete */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Tooltip title={card.name !== "undefined" ? card.name : ""}>
                      <div style={{ maxWidth: "90%", display: "flex", alignItems: "center" }}>
                        <Title
                          level={5}
                          style={{
                            margin: 0,
                            lineHeight: "20px",
                            fontSize: "16px",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {card?.name && card.name !== "undefined"
                            ? card.name.length > 25
                              ? `${card.name.substring(0, 25)}...`
                              : card.name
                            : ""}
                        </Title>
                      </div>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <Button
                        type="text"
                        icon={<DeleteOutlined style={{ color: "#3E767A", fontSize: "16px" }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal({ open: true, subSectionId: card.id });
                        }}
                        style={{
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </Tooltip>
                  </div>

                  <div style={{ height: "4px" }} />

                  {/* Description */}
                  <div>
                    <Tooltip title={getPlainTextSummary(card.description)}>
                      <Title
                        level={5}
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#878787",
                          fontWeight: 400,
                          lineHeight: "18px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {(() => {
                          const plainText = getPlainTextSummary(card.description);
                          return plainText.length > 30 ? `${plainText.substring(0, 30)}...` : plainText;
                        })()}
                      </Title>
                    </Tooltip>
                  </div>
                </div>
              </Card>
            </Col>
          ))}

          {/* ➕ Add New Subsection Card */}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={styles.addCardWrapper} onClick={handleNewSubSection}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 132.746 132.746"
              >
                <path d="M0,0H132.746V132.746H0Z" fill="none" />
                <path
                  d="M62.842,29.655H51.78V51.78H29.655V62.842H51.78V84.966H62.842V62.842H84.966V51.78H62.842ZM57.311,2a55.311,55.311,0,1,0,55.311,55.311A55.331,55.331,0,0,0,57.311,2Zm0,104.56c-26.795,0-49.249-18.474-49.249-49.249S30.147,8.062,57.311,8.062,106.56,26.55,106.56,57.311,84.106,106.56,57.311,106.56Z"
                  transform="translate(9.062 9.062)"
                  fill="#3e767a"
                />
              </svg>
              <span style={{ marginTop: 8, fontSize: 14, color: "#3E767A" }}>Add Subsection</span>
            </div>
          </Col>
        </Row>
      </div>

      {/* Mobile Preview Button (fixed bottom-right) */}
      <div style={styles.mobilePreviewBtnWrapper}>
        <button onClick={() => setShowMobilePreview(true)} style={{ ...styles.greenButton, width: 170 }}>
          Mobile Preview
        </button>
      </div>

      {/* Mobile Preview Drawer */}
      {showMobilePreview && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
            }}
            onClick={() => setShowMobilePreview(false)}
          />

          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "420px",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              boxShadow: "-2px 0 8px rgba(0,0,0,0.3)",
              zIndex: 1001,
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              
              justifyContent: "center",
              alignItems: "center",
              gap: "25px",
            }}
          >
            <div
              style={{
                // flexGrow: 1,
                // display: "flex",
                // justifyContent: "center",
                // alignItems: "flex-start",
                // paddingTop: "1rem",
                maxHeight: "calc(100vh - 140px)",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <SectionMobilePreviewPage />
            </div>
            <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <button
              onClick={() => setShowMobilePreview(false)}
              style={{ ...styles.CloseButton, width: 150, color: "black" }}
            >
              Close Preview
            </button>
            </div>
          </div>
        </>
      )}

      {showUpdateSectionPopup && (
        <NewSectionPopup
          onCloseWarning={closeUpdateSectionPopup}
          onDiscard={handleDiscardSection}
          onSave={handleUpdateSection}
          isSectionSaving={isUpdateSectionSaving}
          mode="Update"
          existingSectionTitle={currentSectionName}
         />
      )}

    </div>
  );
};

export default SectionPage;

// Styles
const baseGreen = "#3E767A";
const styles: { [key: string]: React.CSSProperties } = {
  addCardWrapper: {
    height: "100%",
    minHeight: "250px",
    border: "2px dashed #3E767A",
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    background: "#fafafa",
  },
  mobilePreviewBtnWrapper: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    zIndex: 1000,
  },
  greenButton :
	{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: "8px 16px",
    fontSize: '13px',
    borderRadius: '20px',
    border: `2px solid ${baseGreen}`,
    color: '#F1F2F4',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: baseGreen,
    fontWeight: 600,
    fontFamily: `'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
  },
  whiteButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0px 20px",
    fontSize: "16px",
    borderRadius: "20px",
    border: '2px solid #d9d9d9',
    backgroundColor: '#f8f8f8', 
    color: baseGreen,
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    width: "120px",
    height: "30px",
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
    transform: "translateY(2px)",
    fontFamily: `'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
  },
  okButton: {
    backgroundColor: "#3E767A",
    borderColor: "#3E767A",
    color: "#fff",
  },
  CloseButton : {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: "8px 16px",
    fontSize: '13px',
    borderRadius: '20px',
    border: `2px solid ${baseGreen}`,
    color: '#F1F2F4',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: '#f1f2f4',
    fontWeight: 600,
    fontFamily: `'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
  },
};
